import { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { locales } from '@soroush/shared/locales';
import { clearTokens } from '../lib/api';
import { isItemActive, isNavGroup, siteNav } from '../lib/siteNav';
import { BrandLogo } from './BrandLogo';

function PrimaryNav({
  variant,
  onNavigate,
  menuOpen = false,
}: {
  variant: 'desktop' | 'mobile';
  onNavigate?: () => void;
  menuOpen?: boolean;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  useEffect(() => {
    if (!menuOpen) setOpenGroup(null);
  }, [menuOpen, location.pathname]);

  return (
    <>
      {siteNav.map((entry) => {
        if (!isNavGroup(entry)) {
          const active = isItemActive(entry.to, location.pathname, location.search);
          return (
            <Link
              key={entry.id}
              to={entry.to}
              className={`site-nav__link${active ? ' active' : ''}`}
              aria-current={active ? 'page' : undefined}
              onClick={onNavigate}
            >
              {t(`nav.items.${entry.id}`)}
            </Link>
          );
        }

        const active = entry.items.some((item) =>
          isItemActive(item.to, location.pathname, location.search),
        );
        const open = variant === 'mobile' && openGroup === entry.id;
        const panelId = `site-nav-${variant}-${entry.id}`;

        return (
          <div
            key={entry.id}
            className={`site-nav__group${open ? ' is-open' : ''}${active ? ' is-active' : ''}`}
          >
            <button
              type="button"
              className={`site-nav__link site-nav__toggle${active ? ' active' : ''}`}
              aria-expanded={variant === 'mobile' ? open : undefined}
              aria-controls={panelId}
              aria-haspopup="true"
              onClick={() => {
                if (variant !== 'mobile') return;
                setOpenGroup(open ? null : entry.id);
              }}
            >
              <span>{t(`nav.groups.${entry.id}`)}</span>
              {variant === 'mobile' ? (
                <span className="site-nav__chevron" aria-hidden="true">
                  ▾
                </span>
              ) : null}
            </button>
            <div
              id={panelId}
              className={`site-nav__panel${entry.items.length > 8 ? ' site-nav__panel--wide' : ''}`}
            >
              {entry.items.map((item) => {
                const itemActive = isItemActive(item.to, location.pathname, location.search);
                return (
                  <Link
                    key={item.id}
                    to={item.to}
                    className={`site-nav__sublink${itemActive ? ' active' : ''}`}
                    aria-current={itemActive ? 'page' : undefined}
                    onClick={onNavigate}
                  >
                    {t(`nav.items.${item.id}`)}
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
}

export function SiteHeader() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [solid, setSolid] = useState(location.pathname !== '/');
  const [menuOpen, setMenuOpen] = useState(false);
  const loggedIn = Boolean(localStorage.getItem('sa_access_token'));
  const isHome = location.pathname === '/';
  const menuId = useId();
  const langMenuId = useId();
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const [langOpen, setLangOpen] = useState(false);
  const currentLocale = i18n.language.startsWith('fa') ? 'fa' : 'en';

  useEffect(() => {
    if (!isHome) {
      setSolid(true);
      return;
    }
    const onScroll = () => setSolid(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  useEffect(() => {
    setMenuOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!langOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!langRef.current?.contains(event.target as Node)) setLangOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setLangOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [langOpen]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1081px)');
    const onChange = () => {
      if (mq.matches) setMenuOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    closeBtnRef.current?.focus();

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
      menuBtnRef.current?.focus();
    };
  }, [menuOpen]);

  const headerTone = solid || !isHome || menuOpen ? 'site-header--solid' : 'site-header--transparent';

  const langSwitch = (
    <div className="sa-lang-switch" aria-label={t('common.language')}>
      {locales.map((locale) => (
        <button
          key={locale}
          type="button"
          aria-pressed={i18n.language.startsWith(locale)}
          onClick={() => void i18n.changeLanguage(locale)}
        >
          {locale === 'fa' ? 'فارسی' : 'English'}
        </button>
      ))}
    </div>
  );

  const langCompact = (
    <div className="site-lang" ref={langRef}>
      <button
        type="button"
        className="site-lang__trigger"
        aria-label={t('common.language')}
        aria-haspopup="listbox"
        aria-expanded={langOpen}
        aria-controls={langMenuId}
        onClick={() => setLangOpen((open) => !open)}
      >
        <svg className="site-lang__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path
            d="M3.2 12h17.6M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9s1.3-6.3 3.8-9z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
        <span className="site-lang__abbr">{currentLocale === 'fa' ? 'فا' : 'EN'}</span>
      </button>
      {langOpen ? (
        <ul id={langMenuId} className="site-lang__menu" role="listbox" aria-label={t('common.language')}>
          {locales.map((locale) => {
            const selected = i18n.language.startsWith(locale);
            return (
              <li key={locale} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={selected ? 'is-selected' : undefined}
                  onClick={() => {
                    void i18n.changeLanguage(locale);
                    setLangOpen(false);
                  }}
                >
                  {locale === 'fa' ? 'فارسی' : 'English'}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );

  const authAction = loggedIn ? (
    <button
      type="button"
      className="site-nav__auth"
      onClick={() => {
        clearTokens();
        window.location.href = '/';
      }}
    >
      {t('common.logout')}
    </button>
  ) : (
    <NavLink to="/login" className="site-nav__auth" onClick={() => setMenuOpen(false)}>
      {t('nav.login')}
    </NavLink>
  );

  return (
    <header className={`site-header ${headerTone}${menuOpen ? ' site-header--menu-open' : ''}`}>
      <div className="site-header__inner">
        <div className="site-header__start">
          <BrandLogo variant="wordmark" size="md" priority={isHome} className="site-header__brand" />
          <nav className="site-nav site-nav--desktop" aria-label={t('common.primaryNav')}>
            <PrimaryNav variant="desktop" onNavigate={() => setMenuOpen(false)} />
          </nav>
        </div>

        <div className="site-header__end">
          <div className="site-nav__actions site-nav__actions--desktop">
            {authAction}
            <span className="site-nav__divider" aria-hidden="true" />
            {langCompact}
          </div>

          <div className="site-header__mobile-tools">
            {!loggedIn && (
              <NavLink to="/login" className="site-header__login-chip">
                {t('nav.login')}
              </NavLink>
            )}
            <button
              ref={menuBtnRef}
              type="button"
              className={`site-header__menu-btn${menuOpen ? ' is-open' : ''}`}
              aria-expanded={menuOpen}
              aria-controls={menuId}
              aria-label={menuOpen ? t('common.closeMenu') : t('common.openMenu')}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="site-header__menu-bars" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </button>
          </div>
        </div>
      </div>

      <div
        className={`site-header__scrim${menuOpen ? ' is-open' : ''}`}
        aria-hidden={!menuOpen}
        onClick={() => setMenuOpen(false)}
      />

      <div
        id={menuId}
        className={`site-header__drawer${menuOpen ? ' is-open' : ''}`}
        role="dialog"
        aria-modal={menuOpen}
        aria-hidden={!menuOpen}
        aria-label={t('common.primaryNav')}
        inert={!menuOpen}
      >
        <div className="site-header__drawer-top">
          <BrandLogo variant="wordmark" size="sm" to={null} />
          <button
            ref={closeBtnRef}
            type="button"
            className="site-header__drawer-close"
            aria-label={t('common.closeMenu')}
            onClick={() => setMenuOpen(false)}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <nav className="site-nav site-nav--mobile" aria-label={t('common.primaryNav')}>
          <PrimaryNav variant="mobile" menuOpen={menuOpen} onNavigate={() => setMenuOpen(false)} />
        </nav>

        <div className="site-header__drawer-footer">
          {langSwitch}
          {authAction}
        </div>
      </div>
    </header>
  );
}
